import { useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { ReceiptType } from "@KassaroAB/shared/types/enums/receiptType";
import { LanguageToggle } from "../../components/LanguageToggle";
import { SectionTitle } from "../../components/SectionTitle";
import { InfoBox } from "../../components/InfoBox";
import { Page } from "../../components/Page";
import { Button } from "../../components/Button";
import { LoadingContainer } from "../../components/LoadingContainer";
import { ReceiptDetailPanel } from "../components/receipts/ReceiptDetailPanel";
import { useGetPublicReceiptQuery } from "../../query/publicReceiptQuery";
import { getPublicReceiptPdfApi } from "../../api/publicReceiptApi";
import { ApiError } from "../../utils/api";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { PublicReceiptEmailForm } from "./PublicReceiptEmailForm";
import { getReceiptDisplayNumber } from "@KassaroAB/shared/utils/receiptCalculations";

const NavbarContainer = styled.nav`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
  padding: ${spacing.x2}px ${spacing.x3}px;
  background-color: ${colors.background};
  border-bottom: 1px solid ${colors.border};
  height: 64px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1001;
`;

const NavbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
  flex: 1;
  min-width: 0;
`;

const StoreLogo = styled.img`
  max-height: 40px;
  width: auto;
  max-width: 160px;
  object-fit: contain;
  flex-shrink: 0;
`;

const NavbarTitle = styled.h1`
  margin: 0;
  font-size: ${fontSizes.large}px;
  font-weight: 600;
  color: ${colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: ${fontSizes.regular}px;
  }
`;

const NavbarRight = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: auto;
`;

const publicReceiptNavbar = (
  title?: string,
  storeLogo?: string | null,
  storeName?: string,
) => (
  <NavbarContainer>
    <NavbarLeft>
      {storeLogo ? <StoreLogo src={storeLogo} alt={storeName ?? ""} /> : null}
      {title ? <NavbarTitle>{title}</NavbarTitle> : null}
    </NavbarLeft>
    <NavbarRight>
      <LanguageToggle />
    </NavbarRight>
  </NavbarContainer>
);

const ContentWrapper = styled.div`
  max-width: 640px;
  width: 100%;
  margin: 0 auto;
  padding: ${spacing.x4}px ${spacing.x3}px;
  display: flex;
  flex-direction: column;
  gap: ${spacing.x3}px;

  @media (min-width: 768px) {
    max-width: 960px;
  }
`;

const MainRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x3}px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const ReceiptCard = styled.div`
  background-color: ${colors.background};
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.regular}px;
  padding: ${spacing.x3}px;
  flex: 1;
  min-width: 0;
`;

const ActionsCard = styled(ReceiptCard)`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;

  @media (min-width: 768px) {
    flex: 0 0 280px;
  }
`;

const SectionDescription = styled.p`
  margin: 0;
  font-size: ${fontSizes.regular}px;
  color: ${colors.textMuted};
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${colors.border};
  margin: 0;
`;

const getReceiptTitle = (
  receiptNumber: number | undefined,
  type: ReceiptType,
  t: (key: string) => string,
): string => {
  const typeLabel = t(`statistics.receiptType.${type}`);
  if (receiptNumber) {
    return `${typeLabel} #${receiptNumber}`;
  }
  return typeLabel;
};

export const PublicReceiptView = () => {
  const { t } = useTranslation();
  const { encoded } = useParams<{ encoded: string }>();
  const hash = encoded ? decodeURIComponent(encoded) : null;

  const { data, isLoading, error } = useGetPublicReceiptQuery(hash);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const handleDownloadPdf = () => {
    if (!hash || !data) {
      return;
    }
    setPdfError(null);
    setPdfLoading(true);
    getPublicReceiptPdfApi(hash)
      .then((blob) => {
        const filename = data.receipt.receiptNumber
          ? `kvitto-${data.receipt.receiptNumber}.pdf`
          : "kvitto.pdf";
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError) {
          setPdfError(err.message);
        } else if (err instanceof Error) {
          setPdfError(err.message);
        } else {
          setPdfError(t("publicReceipt.errorGeneric"));
        }
      })
      .finally(() => {
        setPdfLoading(false);
      });
  };

  if (!hash) {
    return (
      <Page centered navbar={publicReceiptNavbar()}>
        <ContentWrapper>
          <InfoBox variant="error">{t("publicReceipt.notFound")}</InfoBox>
        </ContentWrapper>
      </Page>
    );
  }

  if (isLoading) {
    return (
      <Page navbar={publicReceiptNavbar()}>
        <ContentWrapper>
          <LoadingContainer message={t("publicReceipt.loading")} />
        </ContentWrapper>
      </Page>
    );
  }

  if (error || !data) {
    return (
      <Page centered navbar={publicReceiptNavbar()}>
        <ContentWrapper>
          <InfoBox variant="error">
            {error instanceof Error ? error.message : t("publicReceipt.notFound")}
          </InfoBox>
        </ContentWrapper>
      </Page>
    );
  }

  const { receipt, storeLogo, storeName, showArticleNumber, showReferenceOnReceipt, showStaffOnReceipt, showDisplayNameOnReceipt } =
    data;
  const receiptTitle = getReceiptTitle(
    getReceiptDisplayNumber(receipt),
    receipt.type,
    t,
  );

  return (
    <Page navbar={publicReceiptNavbar(receiptTitle, storeLogo, storeName)}>
      <ContentWrapper>
        <MainRow>
          <ReceiptCard>
            <ReceiptDetailPanel
              storeId={receipt.storeId}
              receiptId={receipt.id}
              currency={receipt.total.currency}
              receipt={receipt}
              readOnly
              showArticleNumber={showArticleNumber}
              showReferenceOnReceipt={showReferenceOnReceipt}
              showStaffOnReceipt={showStaffOnReceipt}
              showDisplayNameOnReceipt={showDisplayNameOnReceipt}
            />
          </ReceiptCard>
          <ActionsCard>
            <div>
              <SectionTitle>{t("publicReceipt.saveTitle")}</SectionTitle>
              <SectionDescription>{t("publicReceipt.saveDescription")}</SectionDescription>
            </div>
            {pdfError ? <InfoBox variant="error">{pdfError}</InfoBox> : null}
            <Button disabled={pdfLoading} onClick={handleDownloadPdf}>
              {pdfLoading ? t("publicReceipt.downloading") : t("publicReceipt.downloadPdf")}
            </Button>
            <Divider />
            <PublicReceiptEmailForm
              hash={hash}
              suggestedEmail={receipt.customer?.email?.trim() ?? ""}
            />
          </ActionsCard>
        </MainRow>
      </ContentWrapper>
    </Page>
  );
};
